/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import * as yup from "yup";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import { useLazyGetByCpQuery } from "@api/postalCodes";
import { useLazyGetAddressTypeQuery, useLazyGetPersonTypeQuery } from "@api/catalogs";
import { useLazyGetAddressQuery, usePatchAddressMutation, usePostAddressMutation } from "@api/address";
import { customStylesAsterisk } from "@components/customStylesInputs/inputsStyles";
import { Address, AddressData, PayloadPersonPatch } from "@interfaces/index";
import { InactivityHandler } from "@components/InactivityHandler";
import { ADDRESS_CODES, PERSON_CATALOG } from "@helpers/constantes";

import { useLazyGetLoanApplicationByIdQuery } from "@api/applications";
import { UUID } from "crypto";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { ApiError } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import { useLazyGetPersonsQuery, usePatchPersonMutation } from "@api/personasApi";
import CustomContryCode from "@components/CustomContryCode/CustomContryCode";
import { buildProductRoute } from "@helpers/funciones";
import useSolicitudCreditoForm from "@hooks/useSolicitudCreditoForm";

const SolicitudCreditoPart1PF: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [getByCp] = useLazyGetByCpQuery();
  const [postAddress] = usePostAddressMutation();
  const [patchAddress] = usePatchAddressMutation();

  const { isVisible, isRequired, requiredField } = useSolicitudCreditoForm("step_1");

  const application = useSelector((state: RootState) => state.register.application);
  const clientDetails = useSelector((state: RootState) => state.register.personal_data);

  const [addresTypeParticular, setAddresTypeParticular] = useState<Address | undefined>(undefined);
  const [coloniasParticular, setColoniasParticular] = useState<{ id: number; name: string }[]>([]);

  const [addressParticular, setAddressParticular] = useState<AddressData | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [idRepresentative, setIdRepresentative] = useState<string>("");

  const [getAddress] = useLazyGetAddressQuery();
  const [getAddresType] = useLazyGetAddressTypeQuery();
  const [getApplicationById] = useLazyGetLoanApplicationByIdQuery();
  const [getPersons] = useLazyGetPersonsQuery();
  const [getPersonType] = useLazyGetPersonTypeQuery();
  const [patchPerson] = usePatchPersonMutation();

  const formik = useFormik({
    initialValues: {
      // Datos personales
      email: "",
      country_code: "+52",
      phone: "",
      ocupacion: "",

      // Domicilio particular
      codigoPostal_particular: "",
      calle_particular: "",
      noInterior_particular: "",
      noExterior_particular: "",
      estadoId_particular: "",
      ciudadId_particular: "",
      municipioId_particular: "",
      coloniaId_particular: "",
      estado_particular: "",
      ciudad_particular: "",
      municipio_particular: "",
      otro_particular: "",
      localidadId_particular: "",
      localidad_particular: "",
    },
    validationSchema: yup.object({
      // Datos personales
      ocupacion: requiredField(yup.string(), "ocupacion", true),
      cell_phone_number: requiredField(yup.string().trim(), "cell_phone_number", true),
      phone: requiredField(yup.string().trim(), "cell_phone_number", true),
      email: requiredField(
        yup.string().email("Asegúrate de incluir el @ y los puntos necesarios (.com .mx)"),
        "email",
        true,
      ),

      // Validaciones domicilio particular
      codigoPostal_particular: requiredField(
        yup.string().matches(/^[0-9]{5}$/, "Debe ser un código postal válido"),
        "domicilio_particular",
        true,
        "Requerido",
      ),
      calle_particular: requiredField(yup.string(), "domicilio_particular", true, "Requerido"),
      noInterior_particular: requiredField(yup.string(), "domicilio_particular", false),
      noExterior_particular: requiredField(yup.string(), "domicilio_particular", true, "Requerido"),
      // estadoId_particular no tiene input propio: depende de que el bloque de domicilio esté activo.
      estadoId_particular: requiredField(yup.string(), "domicilio_particular", true, "Requerido"),
      ciudadId_particular: yup.string(),
      municipioId_particular: yup.string(),
      coloniaId_particular: requiredField(yup.string(), "domicilio_particular", true, "Requerido"),
      otro_particular: yup.string().when("coloniaId_particular", {
        is: "-1",
        then: (schema) => requiredField(schema, "domicilio_particular", true),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    onSubmit: async (values) => {
      try {
        setIsLoading(true);
        const payload_address_particular = {
          client_details_id: clientDetails.id,
          address_type_id: addresTypeParticular?.id,
          person_id: idRepresentative,
          cp: values.codigoPostal_particular,
          street: values.calle_particular,
          no_ext: values.noExterior_particular,
          no_int: values.noInterior_particular,
          country_id: 1,
          state_id: values.estadoId_particular ? +values.estadoId_particular : null,
          city_id: values.ciudadId_particular ? +values.ciudadId_particular : null,
          colonia_id: Number(values.coloniaId_particular) === -1 ? null : Number(values.coloniaId_particular),
          other_neighborhood: values.otro_particular,
          locality_id: values.localidadId_particular ? +values.localidadId_particular : null,
        };

        if (isVisible("domicilio_particular")) {
          if (addressParticular?.id) {
            await patchAddress({
              addressId: addressParticular?.id as UUID,
              data: payload_address_particular,
            }).unwrap();
          } else {
            await postAddress(payload_address_particular).unwrap();
          }
        }

        const payload: PayloadPersonPatch = {};
        if (isVisible("ocupacion")) {
          payload.occupation = values.ocupacion;
        }
        if (isVisible("email")) {
          payload.email = values.email;
        }
        if (isVisible("cell_phone_number")) {
          payload.code_phone_number = formik.values.country_code;
          payload.phone_number = values.phone;
        }

        await patchPerson({
          personId: idRepresentative,
          body: payload,
        }).unwrap();

        navigate(buildProductRoute(location.pathname, "solicitud_credito_2PF"));
      } catch (error) {
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setIsLoading(false);
      }
    },
  });

  const savePersonalAddress = async (e: React.FocusEvent<HTMLInputElement>) => {
    try {
      formik.handleBlur(e);
      if (!isVisible("domicilio_particular")) return;
      if (!addresTypeParticular || !clientDetails?.id) return;

      const {
        codigoPostal_particular: codigoPostal_err,
        calle_particular: calle_err,
        noExterior_particular: noExterior_err,
        noInterior_particular: noInterior_err,
        coloniaId_particular: coloniaId_err,
      } = formik.errors;

      if (codigoPostal_err || calle_err || noExterior_err || noInterior_err || coloniaId_err) {
        return;
      }

      const payload_address = {
        client_details_id: clientDetails.id,
        address_type_id: addresTypeParticular.id,
        person_id: idRepresentative,
        cp: formik.values.codigoPostal_particular,
        street: formik.values.calle_particular,
        no_ext: formik.values.noExterior_particular,
        no_int: formik.values.noInterior_particular,
        country_id: 1,
        state_id: formik.values.estadoId_particular ? +formik.values.estadoId_particular : null,
        city_id: formik.values.ciudadId_particular ? +formik.values.ciudadId_particular : null,
        colonia_id:
          Number(formik.values.coloniaId_particular) === -1 ? null : Number(formik.values.coloniaId_particular),
        other_neighborhood: formik.values.otro_particular,
      };

      if (addressParticular?.id) {
        await patchAddress({
          addressId: addressParticular.id as UUID,
          data: payload_address,
        }).unwrap();
      } else {
        const newAddress = await postAddress(payload_address).unwrap();
        setAddressParticular(newAddress.data);
      }
    } catch (error) {
      console.error("Error guardando dirección personal:", error);
    }
  };

  const fetchLocationDataParticular = async (codigoPostal: string, coloniaId?: number) => {
    try {
      if (codigoPostal.length === 5) {
        const value = await getByCp(codigoPostal).unwrap();
        const { colonias, cities, states, localities } = value;
        setColoniasParticular(colonias); // <- usa un state separado para particular

        formik.setFieldValue("estadoId_particular", states[0]?.id || "");
        formik.setFieldValue("estado_particular", states[0]?.name || "");
        formik.setFieldValue("ciudadId_particular", cities[0]?.id || "");
        formik.setFieldValue("ciudad_particular", cities[0]?.name || "");
        formik.setFieldValue("municipioId_particular", localities[0]?.id || "");
        formik.setFieldValue("municipio_particular", localities[0]?.name || "");
        formik.setFieldValue("localidadId_particular", localities[0]?.id || "");
        formik.setFieldValue("localidad_particular", localities[0]?.name || "");

        if (coloniaId) {
          const coloniaSelected = colonias.find((item: any) => item.id == coloniaId);
          formik.setFieldValue("coloniaId_particular", coloniaSelected?.id || "");
        } else {
          formik.setFieldValue("coloniaId_particular", colonias[0]?.id || "");
        }

        setTimeout(() => {
          formik.validateForm();
        }, 200);
      } else {
        formik.setFieldValue("estadoId_particular", "");
        formik.setFieldValue("estado_particular", "");
        formik.setFieldValue("ciudadId_particular", "");
        formik.setFieldValue("ciudad_particular", "");
        formik.setFieldValue("municipioId_particular", "");
        formik.setFieldValue("municipio_particular", "");
        formik.setFieldValue("coloniaId_particular", "");
        formik.setFieldValue("colonia_particular", "");
        formik.setFieldValue("localidadId_particular", "");
        formik.setFieldValue("localidad_particular", "");
      }
    } catch (error) {
      console.error("Error fetching location data (particular):", error);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        if (!application) {
          throw new Error("Solicitud no encontrada");
        }

        const [loan_application, res_address_type, res_persons, res_person_type] = await Promise.all([
          getApplicationById(application.id).unwrap(),
          getAddresType().unwrap(),
          getPersons({
            loanApplicationId: application.id,
          }).unwrap(),
          getPersonType().unwrap(),
        ]);

        if (!loan_application) {
          throw new Error("No se encontró la solicitud de crédito");
        }

        const address_type_particular = res_address_type.find(
          (item: Address) => item.code === ADDRESS_CODES.particular.code,
        );

        const person_type_representative = res_person_type.find(
          (item) => item.code === PERSON_CATALOG.representante_legal.code,
        );

        const person_representative = res_persons.find((item) =>
          item.person_types_detail.some((detail) => detail.id === person_type_representative?.id),
        );

        const address_response_particular = await getAddress({
          personId: String(person_representative?.id),
        }).unwrap();

        const addressParticularPerson = address_response_particular.find(
          (item: AddressData) => item.address_type_id === address_type_particular?.id,
        );

        setAddresTypeParticular(address_type_particular);
        setAddressParticular(addressParticularPerson);
        setIdRepresentative(String(person_representative?.id || ""));

        if (addressParticularPerson) {
          formik.setFieldValue("codigoPostal_particular", addressParticularPerson.cp || "");
          formik.setFieldValue("calle_particular", addressParticularPerson.street || "");
          formik.setFieldValue("noInterior_particular", addressParticularPerson.no_int || "");
          formik.setFieldValue("noExterior_particular", addressParticularPerson.no_ext || "");
          formik.setFieldValue("otro_particular", addressParticularPerson.other_neighborhood || "");
          fetchLocationDataParticular(addressParticularPerson.cp, addressParticularPerson.colonia_id);
        }

        formik.setFieldValue("ocupacion", person_representative?.occupation || "");
        formik.setFieldValue("email", person_representative?.email || "");
        formik.setFieldValue("country_code", person_representative?.code_phone_number || "+52");
        formik.setFieldValue("phone", person_representative?.phone_number || "");

        setTimeout(() => {
          formik.validateForm();
        }, 200);
      } catch (error) {
        const api_error = error as ApiError;
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (String(formik.values.coloniaId_particular) !== "-1") {
      formik.setFieldValue("otro_particular", "");
    }
  }, [formik.values.coloniaId_particular]);

  const handleOnClickRestart = () => {
    navigate(buildProductRoute(location.pathname, "expediente_pf"));
  };

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8}>
        <InactivityHandler />
        <Box ml={3} mr={3}>
          <Box maxWidth="sm" mx="auto">
            <Typography
              mt={4}
              mb={1}
              variant={isMobileOrTablet ? "body2" : "body1"}
              fontWeight={600}
              color={theme?.palette?.primary?.dark}
              textAlign={"center"}
            >
              Solicitud de crédito
            </Typography>
            <Typography
              variant={isMobileOrTablet ? "body2" : "body1"}
              fontWeight={800}
              color={theme?.palette?.primary?.dark}
              textAlign={"center"}
            >
              1{" "}
              <Typography
                component="span"
                variant={isMobileOrTablet ? "body2" : "body1"}
                fontWeight={400}
                color={theme?.palette?.primary?.dark}
              >
                de 3
              </Typography>
            </Typography>

            <form onSubmit={formik.handleSubmit} noValidate>
              <Typography
                textAlign={"center"}
                variant={isMobileOrTablet ? "body2" : "body1"}
                color={theme?.palette?.primary?.dark}
                fontWeight={600}
                my={1}
              >
                Información personal
              </Typography>

              <Grid container spacing={1} mt={2}>
                {isVisible("cell_phone_number") && (
                  <Grid item xs={5} sm={5} md={5}>
                    <CustomContryCode
                      label="Código"
                      name="country_code"
                      value={formik.values.country_code}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.country_code && Boolean(formik.errors.country_code)}
                      disabled={isLoading || formik.isSubmitting}
                      required={isRequired("cell_phone_number", true)}
                    />
                  </Grid>
                )}

                {isVisible("cell_phone_number") && (
                  <Grid item xs={7} sm={7} md={7}>
                    <TextField
                      type="text"
                      name="phone"
                      variant="outlined"
                      label="Teléfono"
                      fullWidth
                      value={formik.values.phone}
                      required={isRequired("cell_phone_number", true)}
                      onChange={(event) => {
                        formik.setFieldValue("phone", event.target.value.replace(/[^0-9]/g, ""));
                      }}
                      onBlur={formik.handleBlur}
                      error={formik.touched.phone && Boolean(formik.errors.phone)}
                      helperText={formik.touched.phone && formik.errors.phone}
                      inputProps={{
                        maxLength: 10,
                        inputMode: "numeric",
                      }}
                      disabled={isLoading || formik.isSubmitting}
                    />
                  </Grid>
                )}
              </Grid>

              {isVisible("email") && (
                <TextField
                  id="email"
                  name="email"
                  label="Correo electrónico"
                  margin="normal"
                  required={isRequired("email", true)}
                  fullWidth
                  autoComplete="off"
                  onBlur={formik.handleBlur}
                  onChange={(e) => {
                    const value_to_set = (e.target.value || "").toLowerCase();
                    formik.setFieldValue("email", value_to_set.trim());
                  }}
                  value={formik.values.email}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email ? formik.errors.email : ""}
                  inputProps={{ maxLength: 100 }}
                  disabled={formik.isSubmitting || isLoading}
                />
              )}

              {isVisible("ocupacion") && (
                <TextField
                  id="ocupacion"
                  name="ocupacion"
                  label="Describe brevemente a qué te dedicas"
                  margin="normal"
                  required={isRequired("ocupacion", true)}
                  fullWidth
                  multiline
                  rows={4}
                  autoComplete="off"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  error={formik.touched.ocupacion && Boolean(formik.errors.ocupacion)}
                  helperText={formik.touched.ocupacion && formik.errors.ocupacion}
                  value={formik.values.ocupacion}
                  disabled={isLoading || formik.isSubmitting}
                />
              )}

              {isVisible("domicilio_particular") && (
                <>
                  <Typography
                    textAlign={"center"}
                    variant={isMobileOrTablet ? "body2" : "body1"}
                    color={theme?.palette?.primary?.dark}
                    fontWeight={600}
                    my={2}
                  >
                    Domicilio
                  </Typography>

                  <FormControl fullWidth>
                    <TextField
                      fullWidth
                      id="codigoPostal_particular"
                      name="codigoPostal_particular"
                      label="Código Postal"
                      required={isRequired("domicilio_particular", true)}
                      value={formik.values.codigoPostal_particular}
                      onChange={(e) => {
                        const cp = e.target.value.replace(/[^0-9]/g, "");
                        formik.setFieldValue("codigoPostal_particular", cp);
                        if (cp.length === 5) {
                          fetchLocationDataParticular(cp);
                        }
                      }}
                      onBlur={savePersonalAddress}
                      error={formik.touched.codigoPostal_particular && Boolean(formik.errors.codigoPostal_particular)}
                      helperText={formik.touched.codigoPostal_particular ? formik.errors.codigoPostal_particular : ""}
                      margin="normal"
                      disabled={isLoading || formik.isSubmitting}
                      inputProps={{ maxLength: 5 }}
                    />
                  </FormControl>

                  <FormControl fullWidth>
                    <TextField
                      fullWidth
                      id="calle_particular"
                      name="calle_particular"
                      label="Calle o avenida"
                      required={isRequired("domicilio_particular", true)}
                      value={formik.values.calle_particular}
                      onChange={formik.handleChange}
                      onBlur={savePersonalAddress}
                      error={formik.touched.calle_particular && Boolean(formik.errors.calle_particular)}
                      helperText={formik.touched.calle_particular ? formik.errors.calle_particular : ""}
                      margin="normal"
                      disabled={isLoading || formik.isSubmitting}
                      inputProps={{ maxLength: 100 }}
                    />
                  </FormControl>

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <TextField
                          fullWidth
                          id="noExterior_particular"
                          name="noExterior_particular"
                          label="No. Exterior"
                          required={isRequired("domicilio_particular", true)}
                          value={formik.values.noExterior_particular}
                          onChange={formik.handleChange}
                          onBlur={savePersonalAddress}
                          error={formik.touched.noExterior_particular && Boolean(formik.errors.noExterior_particular)}
                          helperText={formik.touched.noExterior_particular ? formik.errors.noExterior_particular : ""}
                          margin="normal"
                          disabled={isLoading || formik.isSubmitting}
                          inputProps={{ maxLength: 20 }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <TextField
                          fullWidth
                          id="noInterior_particular"
                          name="noInterior_particular"
                          label="No. Interior"
                          required={isRequired("domicilio_particular", false)}
                          value={formik.values.noInterior_particular}
                          onChange={formik.handleChange}
                          onBlur={savePersonalAddress}
                          error={formik.touched.noInterior_particular && Boolean(formik.errors.noInterior_particular)}
                          helperText={formik.touched.noInterior_particular ? formik.errors.noInterior_particular : ""}
                          margin="normal"
                          disabled={isLoading || formik.isSubmitting}
                          inputProps={{ maxLength: 20 }}
                        />
                      </FormControl>
                    </Grid>
                  </Grid>

                  <FormControl
                    fullWidth
                    required={isRequired("domicilio_particular", true)}
                    sx={{
                      ...customStylesAsterisk,
                      mt: 1,
                    }}
                    error={formik.touched.coloniaId_particular && Boolean(formik.errors.coloniaId_particular)}
                  >
                    <InputLabel id={"coloniaId_particular"}>Colonia</InputLabel>
                    <Select
                      labelId="coloniaId_particular"
                      name="coloniaId_particular"
                      id="coloniaId_particular"
                      fullWidth
                      required={isRequired("domicilio_particular", true)}
                      label="Colonia"
                      value={formik.values.coloniaId_particular}
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      error={formik.touched.coloniaId_particular && Boolean(formik.errors.coloniaId_particular)}
                      sx={{ textAlign: "left" }}
                      disabled={isLoading || formik.isSubmitting}
                    >
                      <MenuItem value="">Selecciona</MenuItem>
                      {coloniasParticular.map((colonia) => (
                        <MenuItem key={colonia.id} value={colonia.id}>
                          {colonia.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {formik.touched.coloniaId_particular && formik.errors.coloniaId_particular && (
                      <FormHelperText>{formik.errors.coloniaId_particular}</FormHelperText>
                    )}
                  </FormControl>

                  {String(formik.values.coloniaId_particular) === "-1" && (
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={12} md={12}>
                        <TextField
                          id="otro_particular"
                          name="otro_particular"
                          label="Otra Colonia"
                          margin="normal"
                          required={isRequired("domicilio_particular", true)}
                          fullWidth
                          autoComplete="off"
                          onBlur={savePersonalAddress}
                          onChange={formik.handleChange}
                          error={formik.touched.otro_particular && Boolean(formik.errors.otro_particular)}
                          helperText={formik.touched.otro_particular && formik.errors.otro_particular}
                          value={formik.values.otro_particular}
                        />
                      </Grid>
                    </Grid>
                  )}

                  <FormControl fullWidth>
                    <TextField
                      fullWidth
                      id="ciudad_particular"
                      name="ciudad_particular"
                      label="Ciudad"
                      value={formik.values.ciudad_particular}
                      error={formik.touched.ciudad_particular && Boolean(formik.errors.ciudad_particular)}
                      helperText={formik.touched.ciudad_particular ? formik.errors.ciudad_particular : ""}
                      margin="normal"
                      disabled={true}
                    />
                  </FormControl>

                  <FormControl fullWidth>
                    <TextField
                      fullWidth
                      id="municipio_particular"
                      name="municipio_particular"
                      label="Municipio"
                      value={formik.values.localidad_particular}
                      error={formik.touched.localidadId_particular && Boolean(formik.errors.localidadId_particular)}
                      helperText={formik.touched.localidadId_particular ? formik.errors.localidadId_particular : ""}
                      margin="normal"
                      disabled={true}
                    />
                  </FormControl>

                  <FormControl fullWidth>
                    <TextField
                      fullWidth
                      id="estado_particular"
                      name="estado_particular"
                      label="Entidad federativa"
                      value={formik.values.estado_particular}
                      error={formik.touched.estado_particular && Boolean(formik.errors.estado_particular)}
                      helperText={formik.touched.estado_particular ? formik.errors.estado_particular : ""}
                      margin="normal"
                      disabled={true}
                    />
                  </FormControl>
                </>
              )}

              <Grid container columnSpacing={1} my={4}>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="blue_outlined"
                    sx={{ padding: "0", height: "100%" }}
                    onClick={handleOnClickRestart}
                    disabled={formik.isSubmitting}
                  >
                    Regresar al inicio
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    type="submit"
                    variant="default"
                    fullWidth
                    color="primary"
                    disabled={!formik.isValid || formik.isSubmitting}
                  >
                    Continuar
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Box>
      </Grid>
      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default SolicitudCreditoPart1PF;
